<template>
  <section class="game-player">
    <header>
      <button :aria-label="$t('gameUi.close')" @click="$emit('close')"><i class="el-icon-arrow-left" /></button>
      <div><small>{{ $t(game.game_type === "external" ? "gameUi.externalGame" : "gameUi.internalGame") }}</small><strong>{{ game.title }}</strong></div>
      <div class="game-header-actions"><button :aria-label="$t('a11y.share')" @click="$emit('share', game)"><i class="el-icon-share" /></button><button :aria-label="$t(game.collected ? 'gameUi.removeFavorite' : 'gameUi.addFavorite')" @click="$emit('favorite', game)"><i :class="game.collected ? 'el-icon-star-on active' : 'el-icon-star-off'" /></button></div>
    </header>

    <main v-if="launch.type === 'external'" class="external-game">
      <iframe
        :src="launch.url"
        :title="game.title"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        allow="fullscreen"
        referrerpolicy="no-referrer"
      />
      <aside>
        <span><i class="el-icon-warning-outline" />{{ $t("gameUi.externalTip") }}</span>
        <button @click="openExternal"><i class="el-icon-top-right" />{{ $t("gameUi.openBrowser") }}</button>
      </aside>
    </main>

    <main v-else-if="launch.internal_code === 'reaction'" class="reaction-game">
      <div class="game-atmosphere"><i /><i /><i /></div>
      <section>
        <small>{{ $t("gameUi.roundProgress", { current: Math.min(reactionRound + 1, 5), total: 5 }) }}</small>
        <h1>{{ $t("gameUi.reactionTitle") }}</h1>
        <p>{{ $t("gameUi.reactionHint") }}</p>
        <button
          class="reaction-orb"
          :class="`state-${reactionState}`"
          @click="tapReaction"
        >
          <i :class="reactionState === 'ready' ? 'el-icon-position' : 'el-icon-thumb'" />
          <strong>{{ $t(`gameUi.reactionState.${reactionState}`) }}</strong>
          <em v-if="reactionLast !== null">{{ $t("gameUi.milliseconds", { value: reactionLast }) }}</em>
        </button>
        <div class="reaction-stats">
          <span><small>{{ $t("gameUi.bestReaction") }}</small><strong>{{ reactionBest === null ? "—" : $t("gameUi.milliseconds", { value: reactionBest }) }}</strong></span>
          <span><small>{{ $t("gameUi.averageReaction") }}</small><strong>{{ reactionAverage === null ? "—" : $t("gameUi.milliseconds", { value: reactionAverage }) }}</strong></span>
        </div>
        <button v-if="reactionState === 'idle' || reactionState === 'finished'" class="game-primary" @click="startReaction">
          {{ $t(reactionState === "finished" ? "gameUi.playAgain" : "gameUi.startGame") }}
        </button>
      </section>
    </main>

    <main v-else class="memory-game">
      <div class="memory-head">
        <div><small>{{ $t("gameUi.memoryMoves") }}</small><strong>{{ memoryMoves }}</strong></div>
        <h1>{{ $t("gameUi.memoryTitle") }}</h1>
        <div><small>{{ $t("gameUi.memoryPairs") }}</small><strong>{{ memoryMatched / 2 }}/8</strong></div>
      </div>
      <p>{{ $t("gameUi.memoryHint") }}</p>
      <section class="memory-grid">
        <button
          v-for="card in memoryCards"
          :key="card.id"
          :class="{ revealed: card.revealed || card.matched, matched: card.matched }"
          :disabled="memoryLocked || card.matched"
          :aria-label="$t(card.revealed || card.matched ? 'gameUi.cardRevealed' : 'gameUi.cardHidden')"
          @click="flipMemory(card)"
        >
          <span>{{ card.revealed || card.matched ? card.symbol : "✦" }}</span>
        </button>
      </section>
      <button class="game-primary" @click="resetMemory">{{ $t(memoryStarted ? "gameUi.restartGame" : "gameUi.startGame") }}</button>
    </main>
  </section>
</template>

<script>
export default {
  name: "GamePlayer",
  props: {
    game: { type: Object, required: true },
    launch: { type: Object, required: true }
  },
  data() {
    return {
      startedAt: Date.now(),
      reactionState: "idle",
      reactionRound: 0,
      reactionStartedAt: 0,
      reactionTimes: [],
      reactionLast: null,
      reactionTimer: null,
      memoryCards: [],
      memoryFlipped: [],
      memoryMoves: 0,
      memoryMatched: 0,
      memoryLocked: false,
      memoryStarted: false
    }
  },
  computed: {
    reactionBest() {
      return this.reactionTimes.length ? Math.min(...this.reactionTimes) : null
    },
    reactionAverage() {
      return this.reactionTimes.length
        ? Math.round(this.reactionTimes.reduce((sum, value) => sum + value, 0) / this.reactionTimes.length)
        : null
    }
  },
  created() {
    if (this.launch.internal_code === "memory") this.prepareMemory()
  },
  beforeDestroy() {
    window.clearTimeout(this.reactionTimer)
  },
  methods: {
    openExternal() {
      window.open(this.launch.url, "_blank", "noopener,noreferrer")
    },
    startReaction() {
      window.clearTimeout(this.reactionTimer)
      this.reactionRound = 0
      this.reactionTimes = []
      this.reactionLast = null
      this.queueReaction()
    },
    queueReaction() {
      this.reactionState = "waiting"
      const delay = 900 + Math.floor(Math.random() * 1800)
      this.reactionTimer = window.setTimeout(() => {
        this.reactionState = "ready"
        this.reactionStartedAt = performance.now()
      }, delay)
    },
    tapReaction() {
      if (this.reactionState === "waiting") {
        window.clearTimeout(this.reactionTimer)
        this.reactionState = "early"
        this.reactionTimer = window.setTimeout(this.queueReaction, 850)
        return
      }
      if (this.reactionState !== "ready") return

      const elapsed = Math.max(1, Math.round(performance.now() - this.reactionStartedAt))
      this.reactionLast = elapsed
      this.reactionTimes.push(elapsed)
      this.reactionRound += 1
      if (this.reactionRound >= 5) {
        this.reactionState = "finished"
        const score = Math.max(0, 1200 - this.reactionAverage)
        this.finish(score)
      } else {
        this.reactionState = "result"
        this.reactionTimer = window.setTimeout(this.queueReaction, 800)
      }
    },
    prepareMemory() {
      const symbols = ["✦", "◆", "●", "▲", "☾", "♬", "⚡", "♥"]
      this.memoryCards = symbols.concat(symbols)
        .map((symbol, index) => ({ id: `${symbol}-${index}`, symbol, revealed: false, matched: false, order: Math.random() }))
        .sort((left, right) => left.order - right.order)
      this.memoryFlipped = []
      this.memoryMoves = 0
      this.memoryMatched = 0
      this.memoryLocked = false
    },
    resetMemory() {
      this.prepareMemory()
      this.memoryStarted = true
      this.startedAt = Date.now()
    },
    flipMemory(card) {
      if (!this.memoryStarted) this.resetMemory()
      const selected = this.memoryCards.find(item => item.id === card.id)
      if (!selected || selected.revealed || selected.matched || this.memoryLocked) return
      this.$set(selected, "revealed", true)
      this.memoryFlipped.push(selected)
      if (this.memoryFlipped.length < 2) return

      this.memoryMoves += 1
      const [first, second] = this.memoryFlipped
      if (first.symbol === second.symbol) {
        this.$set(first, "matched", true)
        this.$set(second, "matched", true)
        this.memoryMatched += 2
        this.memoryFlipped = []
        if (this.memoryMatched === this.memoryCards.length) {
          const seconds = Math.max(1, Math.round((Date.now() - this.startedAt) / 1000))
          this.finish(Math.max(100, 5000 - this.memoryMoves * 100 - seconds * 20))
        }
        return
      }

      this.memoryLocked = true
      window.setTimeout(() => {
        this.$set(first, "revealed", false)
        this.$set(second, "revealed", false)
        this.memoryFlipped = []
        this.memoryLocked = false
      }, 650)
    },
    finish(score) {
      this.$emit("finish", {
        game_id: this.game.id,
        score,
        play_seconds: Math.max(1, Math.round((Date.now() - this.startedAt) / 1000)),
        completed: 1
      })
    }
  }
}
</script>

<style scoped>
.game-player { --accent:#ff4d67; --aqua:#35e4d4; position:absolute; z-index:95; inset:0; overflow:hidden; color:#fff; background:#07080d; }
.game-player button { border:0; color:inherit; background:none; font:inherit; cursor:pointer; }
.game-player > header { position:relative; z-index:3; height:60px; display:grid; grid-template-columns:52px minmax(0,1fr) 82px; align-items:center; padding-top:env(safe-area-inset-top); border-bottom:1px solid rgba(255,255,255,.08); background:rgba(7,8,13,.9); backdrop-filter:blur(18px); }
.game-player > header button { height:48px; font-size:18px; }
.game-player > header div { min-width:0; text-align:center; }
.game-player > header .game-header-actions{display:grid;grid-template-columns:1fr 1fr}.game-player > header .game-header-actions button{padding:0;font-size:16px}
.game-player > header small,.game-player > header strong { display:block; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.game-player > header small { color:var(--aqua); font-size:7px; letter-spacing:1.2px; text-transform:uppercase; }
.game-player > header strong { margin-top:3px; font-size:11px; }
.game-player > header .active { color:var(--accent); }
.game-player > main { height:calc(100% - 60px - env(safe-area-inset-top)); }
.external-game { position:relative; display:flex; flex-direction:column; }
.external-game iframe { width:100%; min-height:0; flex:1; border:0; background:#fff; }
.external-game aside { min-height:56px; display:flex; gap:12px; align-items:center; justify-content:space-between; padding:8px 14px calc(8px + env(safe-area-inset-bottom)); color:rgba(255,255,255,.58); background:#101218; font-size:8px; }
.external-game aside span { min-width:0; display:flex; gap:6px; align-items:center; line-height:1.4; }
.external-game aside button { flex:0 0 auto; padding:8px 11px; border-radius:16px; color:#07080d; background:#fff; font-size:8px; font-weight:900; }
.reaction-game { position:relative; display:grid; place-items:center; overflow:hidden; background:radial-gradient(circle at 50% 35%,rgba(53,228,212,.13),transparent 35%),linear-gradient(160deg,#111329,#07080d 62%); }
.game-atmosphere,.game-atmosphere i { position:absolute; inset:0; }
.game-atmosphere i { border:1px solid rgba(53,228,212,.09); border-radius:50%; transform:scale(.65); animation:gamePulse 5s ease-in-out infinite; }
.game-atmosphere i:nth-child(2) { transform:scale(.9); animation-delay:-1.4s; }
.game-atmosphere i:nth-child(3) { transform:scale(1.18); animation-delay:-2.8s; }
.reaction-game > section { position:relative; z-index:2; width:min(88%,420px); text-align:center; }
.reaction-game section > small { color:var(--aqua); font-size:8px; font-weight:900; letter-spacing:1.5px; }
.reaction-game h1,.memory-head h1 { margin:9px 0 5px; font-size:25px; letter-spacing:-.8px; }
.reaction-game p,.memory-game > p { margin:0 auto 20px; color:rgba(255,255,255,.5); font-size:9px; line-height:1.6; }
.reaction-orb { width:min(58vw,230px); height:min(58vw,230px); display:flex; flex-direction:column; align-items:center; justify-content:center; margin:24px auto; border:1px solid rgba(255,255,255,.16)!important; border-radius:50%; background:radial-gradient(circle at 35% 28%,rgba(255,255,255,.2),rgba(255,77,103,.22) 35%,rgba(255,77,103,.06) 70%)!important; box-shadow:0 30px 80px rgba(0,0,0,.45),inset 0 0 50px rgba(255,255,255,.04); transition:transform .16s ease,background .2s ease,box-shadow .2s ease; }
.reaction-orb.state-ready { color:#06110f; background:radial-gradient(circle at 35% 28%,#fff,var(--aqua) 35%,#12a99b 72%)!important; box-shadow:0 0 60px rgba(53,228,212,.46); transform:scale(1.03); }
.reaction-orb.state-early { background:radial-gradient(circle at 35% 28%,#fff,#ffbc51 35%,#c86928 72%)!important; }
.reaction-orb i { font-size:31px; }
.reaction-orb strong { margin-top:11px; font-size:13px; }
.reaction-orb em { margin-top:6px; font-size:10px; font-style:normal; }
.reaction-stats { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin:0 auto 18px; }
.reaction-stats span { padding:11px; border:1px solid rgba(255,255,255,.08); border-radius:13px; background:rgba(255,255,255,.04); }
.reaction-stats small,.reaction-stats strong { display:block; }
.reaction-stats small { color:rgba(255,255,255,.42); font-size:7px; }
.reaction-stats strong { margin-top:5px; font-size:12px; }
.game-primary { min-width:150px; padding:11px 20px!important; border-radius:22px; color:#07080d!important; background:#fff!important; font-size:10px!important; font-weight:900!important; box-shadow:0 10px 28px rgba(0,0,0,.3); }
.memory-game { overflow-y:auto; padding:22px 18px calc(25px + env(safe-area-inset-bottom)); text-align:center; background:radial-gradient(circle at 8% 0,rgba(255,77,103,.16),transparent 35%),linear-gradient(160deg,#181022,#08090d 60%); }
.memory-head { max-width:460px; display:grid; grid-template-columns:70px 1fr 70px; gap:8px; align-items:center; margin:0 auto; }
.memory-head > div { padding:9px 4px; border:1px solid rgba(255,255,255,.09); border-radius:12px; background:rgba(255,255,255,.04); }
.memory-head small,.memory-head strong { display:block; }
.memory-head small { color:rgba(255,255,255,.45); font-size:7px; }
.memory-head strong { margin-top:3px; color:var(--aqua); font-size:13px; }
.memory-grid { max-width:430px; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:9px; margin:18px auto 22px; perspective:900px; }
.memory-grid button { aspect-ratio:1; display:grid; place-items:center; border:1px solid rgba(255,255,255,.11)!important; border-radius:15px; color:rgba(255,255,255,.48); background:linear-gradient(145deg,#1d2030,#11131d)!important; box-shadow:0 10px 22px rgba(0,0,0,.25); font-size:22px; transition:transform .25s ease,background .25s ease,color .25s ease; }
.memory-grid button.revealed { color:#07100f; background:linear-gradient(145deg,#d8fffb,var(--aqua))!important; transform:rotateY(180deg); }
.memory-grid button.matched { color:#fff; background:linear-gradient(145deg,#ff8d9d,var(--accent))!important; box-shadow:0 0 24px rgba(255,77,103,.24); }
[dir="rtl"] .game-player > header div { direction:rtl; }
@keyframes gamePulse { 0%,100% { opacity:.25; transform:scale(.68); } 50% { opacity:.75; transform:scale(.82); } }
@media (max-height:650px) {
  .reaction-orb { width:180px; height:180px; margin:14px auto; }
  .memory-grid { max-width:330px; gap:7px; }
}
</style>
